<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { api, type PricingSourceFund } from '../api/client'
import { useFundEnablement } from '../composables/useFundEnablement'
import { pct } from './pricingSourceHelpers'
import { GATED_FUNDS } from './nav/fundConfig'

// Red dashboard alert for the stale-NAV pricing-source detector (spec
// 2026-09-25): shown on every tab while any fund the user trades is Red.
// Amber/Green render nothing; fetch errors render nothing (console only).
const props = withDefaults(defineProps<{ linkToNav?: boolean }>(), { linkToNav: true })
const { hasAccess, allocations } = useFundEnablement()
const funds = ref<PricingSourceFund[]>([])
let timer: ReturnType<typeof setInterval> | null = null

async function poll() {
  try {
    // A payload without a funds array leaves the banner empty instead of throwing in redFunds.
    const res = await api.pricingSource()
    funds.value = Array.isArray(res?.funds) ? res.funds : []
  } catch (e) {
    console.warn('pricing-source alert poll failed', e)
  }
}

onMounted(() => { poll(); timer = setInterval(poll, 600_000) })
onUnmounted(() => { if (timer) clearInterval(timer) })

// This banner is for TRADED funds only. A gated fund (JAPAN today) has a
// pricing-source row (since spec 2026-09-29) but nobody trades it yet (dark,
// entitlement-gated fund — docs/runbooks/japan-fund-dark.md) — explicit
// exclusion rather than relying on the generic fallback below, so it can't
// accidentally start alerting the moment the row exists. Derived from the
// registry (PR 0, spec 2026-10-05) so a future gated fund is excluded the
// same way without a new literal. Revisit when a gated fund is
// granted/traded (six-fund shared-pot Phase 3+).
const EXCLUDED_FROM_BANNER = new Set<string>(GATED_FUNDS)

const redFunds = computed(() => funds.value.filter(f => {
  if (f.rag !== 'red') return false
  if (EXCLUDED_FROM_BANNER.has(f.label)) return false
  if (f.label === 'GGE') return hasAccess('GGE') && allocations.value.some(a => a.fund === 'GGE' && a.enabled)
  return hasAccess(f.label)
}))
</script>

<template>
  <div v-if="redFunds.length" class="psd-alert" role="alert" data-testid="pricing-source-alert">
    <span class="psd-alert-icon">⛔</span>
    <div class="psd-alert-body">
      <div class="psd-alert-title">STALE-NAV EDGE ALERT</div>
      <div v-for="f in redFunds" :key="f.label" class="psd-alert-msg">
        {{ f.label }}'s price now appears to use live US prices (40-day pass-through {{ pct(f.passThrough40) }}).
        Review whether to keep trading it.
      </div>
      <a v-if="props.linkToNav" href="#nav" class="psd-alert-link">View in NAV</a>
      <span v-else class="psd-alert-link">Details: switch to a fund portfolio's NAV tab.</span>
    </div>
  </div>
</template>

<style scoped>
.psd-alert { display: flex; align-items: center; gap: 0.75rem; background: #b71c1c; color: #fff;
  border: 2px solid #f44336; border-radius: 6px; padding: 0.75rem 1rem; margin-bottom: 0.6rem; font-weight: 700; }
.psd-alert-icon { font-size: 1.5rem; line-height: 1; }
.psd-alert-body { display: flex; flex-direction: column; gap: 0.15rem; }
.psd-alert-title { font-size: 0.95rem; letter-spacing: 0.03em; }
.psd-alert-msg { font-size: 0.85rem; font-weight: 600; }
.psd-alert-link { color: #fff; text-decoration: underline; font-size: 0.8rem; }
</style>
